/**
 * Product Form Types - Story 6.3: Create Add Product Form
 * Comprehensive type definitions for multi-step product creation form
 */

/**
 * Product Category Enum
 * Available categories for products in the marketplace
 * Note: This extends beyond the existing ProductCategory type to include
 * additional categories specific to the Gaushala marketplace
 */
export enum ProductCategory {
  GAUSHALA_PRODUCTS = 'GAUSHALA_PRODUCTS',
  CATTLE = 'CATTLE',
  EQUIPMENT = 'EQUIPMENT',
  FEED = 'FEED',
  SERVICES = 'SERVICES',
  OTHER = 'OTHER',
}

// ============================================================================
// Form Step Data Types
// ============================================================================

/**
 * Step 1: Product Details Form Data
 * Basic information about the product
 */
export interface ProductDetailsFormData {
  /** Product name (3-100 characters) */
  name: string;
  /** Product category from predefined list */
  category: ProductCategory;
  /** Detailed product description (20-2000 characters) */
  description: string;
}

/**
 * Step 2: Pricing Form Data
 * Price, unit, and quantity information
 */
export interface PricingFormData {
  /** Product price in INR (must be positive) */
  price: number;
  /** Unit of measurement (kg, liter, piece, etc.) */
  unit: string;
  /** Available quantity (must be positive integer) */
  quantity: number;
  /** Minimum order quantity (optional, must be positive integer) */
  minimumOrderQuantity?: number;
}

/**
 * Image Interface
 * Represents an image with its file and preview URL
 */
export interface Image {
  /** The actual file object (null for existing/uploaded images) */
  file: File | null;
  /** Base64 data URL or server URL for preview display */
  preview: string;
  /** Optional ID for existing images */
  id?: string;
  /** Server URL after upload (used for form submission, separate from preview) */
  serverUrl?: string;
}

/**
 * Step 3: Images Form Data
 * Product images (1-5 images, max 5MB each)
 */
export interface ImagesFormData {
  /** Array of product images */
  images: Image[];
}

/**
 * Step 4: Location Form Data
 * Delivery/pickup location details
 */
export interface LocationFormData {
  /** Indian state */
  state: string;
  /** District within the state */
  district: string;
  /** 6-digit pincode */
  pincode: string;
  /** Optional detailed address */
  address?: string;
}

/**
 * Step 5: Review Form Data
 * Aggregated data for review step (read-only view)
 */
export interface ReviewFormData {
  productDetails: ProductDetailsFormData;
  pricing: PricingFormData;
  images: ImagesFormData;
  location: LocationFormData;
}

// ============================================================================
// Complete Form Data Types
// ============================================================================

/**
 * Complete Product Form Data
 * Union of all step data for the entire form
 */
export interface ProductFormData {
  // Product Details (Step 1)
  name: string;
  category: ProductCategory;
  description: string;
  // Pricing (Step 2)
  price: number;
  unit: string;
  quantity: number;
  minimumOrderQuantity?: number;
  // Images (Step 3)
  images: Image[];
  // Location (Step 4)
  state: string;
  district: string;
  pincode: string;
  address?: string;
}

/**
 * Initial/Default Form Data
 * Used for form initialization and reset
 */
export interface InitialProductFormData {
  name: string;
  category: ProductCategory | '';
  description: string;
  price: number | '';
  unit: string;
  quantity: number | '';
  minimumOrderQuantity?: number | '';
  images: Image[];
  state: string;
  district: string;
  pincode: string;
  address: string;
}

// ============================================================================
// API Request/Response Types
// ============================================================================

/**
 * Create Product API Request
 * Payload sent to the server to create a new product
 */
export interface CreateProductRequest {
  /** Product name */
  name: string;
  /** Product category */
  category: ProductCategory;
  /** Product description */
  description: string;
  /** Price in INR */
  price: number;
  /** Unit of measurement */
  unit: string;
  /** Available quantity */
  quantity: number;
  /** Minimum order quantity (optional) */
  minimumOrderQuantity?: number;
  /** State for delivery */
  state: string;
  /** District for delivery */
  district: string;
  /** Pincode for delivery */
  pincode: string;
  /** Optional detailed address */
  address?: string;
  /** Array of uploaded image IDs */
  imageIds?: string[];
}

/**
 * Create Product API Response
 * Response from server after successful product creation
 */
export interface CreateProductResponse {
  /** Unique product identifier */
  id: string;
  /** Product name */
  name: string;
  /** Product category */
  category: ProductCategory;
  /** Product status (typically 'PENDING' or 'ACTIVE') */
  status: string;
  /** ISO timestamp of creation */
  createdAt: string;
  /** ISO timestamp of last update */
  updatedAt: string;
}

/**
 * Upload Image API Response
 * Response from server after successful image upload
 */
export interface UploadImageResponse {
  /** Unique image identifier */
  imageId: string;
  /** Public URL of the uploaded image */
  url: string;
  /** ISO timestamp of upload */
  uploadedAt: string;
}

/**
 * Upload Image Error Response
 * Error response from image upload
 */
export interface UploadImageErrorResponse {
  /** Error message */
  message: string;
  /** Error code */
  errorCode?: string;
  /** File name that failed */
  fileName?: string;
}

// ============================================================================
// Hook Return Types
// ============================================================================

/**
 * Form Validation Errors
 * Map of field names to error messages
 */
export type FormErrors = Record<string, string>;

/**
 * UseProductForm Hook Return Type
 * Return value of the useProductForm custom hook
 */
export interface UseProductFormReturn {
  /** Current form data across all steps */
  formData: ProductFormData;
  /** Current active step (1-5) */
  currentStep: number;
  /** Total number of steps */
  totalSteps: number;
  /** Navigate to next step (validates current step first) */
  goToNextStep: () => Promise<boolean>;
  /** Navigate to previous step */
  goToPreviousStep: () => void;
  /** Navigate to a specific step */
  goToStep: (step: number) => void;
  /** Submit the complete form */
  submitForm: () => Promise<CreateProductResponse>;
  /** Reset form to initial state */
  resetForm: () => void;
  /** Flag indicating form submission in progress */
  isSubmitting: boolean;
  /** Flag indicating validation in progress */
  isValidating: boolean;
  /** Current validation errors */
  errors: FormErrors;
  /** Update form data (partial updates supported) */
  setFormData: (data: Partial<ProductFormData>) => void;
  /** Clear errors for specific field or all */
  clearErrors: (field?: string) => void;
  /** Check if current step is valid */
  isCurrentStepValid: () => Promise<boolean>;
  /** Check if form has unsaved changes */
  hasUnsavedChanges: boolean;
}

/**
 * UseImageUpload Hook Return Type
 * Return value of the useImageUpload custom hook
 */
export interface UseImageUploadReturn {
  /** Array of images (local files with previews) */
  images: Image[];
  /** Add new images to the array */
  addImages: (files: File[]) => void;
  /** Remove an image by index */
  removeImage: (index: number) => void;
  /** Reorder images (drag and drop) */
  reorderImages: (fromIndex: number, toIndex: number) => void;
  /** Flag indicating upload in progress */
  isUploading: boolean;
  /** Progress percentage (0-100) */
  uploadProgress: number;
  /** Array of successfully uploaded image IDs */
  uploadedImageIds: string[];
  /** Upload all images to server */
  uploadImages: () => Promise<string[]>;
  /** Clear all images */
  clearImages: () => void;
  /** Error message if upload failed */
  uploadError: string | null;
}

/**
 * UseLocationData Hook Return Type
 * Return value for location data management
 */
export interface UseLocationDataReturn {
  /** List of available states */
  states: string[];
  /** List of districts for selected state */
  districts: string[];
  /** Currently selected state */
  selectedState: string;
  /** Set the selected state (also clears district) */
  setSelectedState: (state: string) => void;
  /** Flag indicating if districts are loading */
  isLoadingDistricts: boolean;
}

// ============================================================================
// Form Step Configuration Types
// ============================================================================

/**
 * Form Step Configuration
 * Defines metadata for each step in the form wizard
 */
export interface FormStepConfig {
  /** Step number (1-indexed) */
  step: number;
  /** Step title */
  title: string;
  /** Step description */
  description: string;
  /** Lucide icon name */
  icon: string;
}

/**
 * Form Step Status
 * Tracks the completion status of each step
 */
export interface FormStepStatus {
  /** Step number */
  step: number;
  /** Whether the step has been visited */
  visited: boolean;
  /** Whether the step is complete and valid */
  completed: boolean;
  /** Whether the step has errors */
  hasErrors: boolean;
}

// ============================================================================
// Component Props Types
// ============================================================================

/**
 * Add Product Form Props
 * Props for the main AddProductForm component
 */
export interface AddProductFormProps {
  /** Callback when product is successfully created */
  onSuccess?: (product: CreateProductResponse) => void;
  /** Callback when form is cancelled */
  onCancel?: () => void;
  /** Initial form data for editing (future use) */
  initialData?: Partial<ProductFormData>;
}

/**
 * Form Step Component Props
 * Base props for individual step components
 */
export interface FormStepProps<T> {
  /** Form data for this step */
  data: T;
  /** Callback to update form data */
  onChange: (data: Partial<T>) => void;
  /** Validation errors for this step */
  errors: FormErrors;
  /** Whether the step is disabled */
  disabled?: boolean;
}

/**
 * Product Details Step Props
 */
export type ProductDetailsStepProps = FormStepProps<ProductDetailsFormData>;

/**
 * Pricing Step Props
 */
export type PricingStepProps = FormStepProps<PricingFormData>;

/**
 * Images Step Props
 */
export interface ImagesStepProps extends FormStepProps<ImagesFormData> {
  /** Maximum number of images allowed */
  maxImages?: number;
  /** Maximum file size in bytes */
  maxFileSize?: number;
}

/**
 * Location Step Props
 */
export interface LocationStepProps extends FormStepProps<LocationFormData> {
  /** Available states */
  states: string[];
  /** Available districts for selected state */
  districts: string[];
  /** Whether districts are loading */
  isLoadingDistricts?: boolean;
}

/**
 * Review Step Props
 */
export interface ReviewStepProps {
  /** Complete form data for review */
  formData: ProductFormData;
  /** Callback to navigate to a specific step for editing */
  onEditStep: (step: number) => void;
}

/**
 * Form Navigation Props
 * Props for form navigation buttons
 */
export interface FormNavigationProps {
  /** Current step number */
  currentStep: number;
  /** Total number of steps */
  totalSteps: number;
  /** Go to previous step handler */
  onPrevious: () => void;
  /** Go to next step handler */
  onNext: () => void;
  /** Submit form handler */
  onSubmit: () => void;
  /** Cancel form handler */
  onCancel: () => void;
  /** Whether next/submit is disabled */
  isDisabled?: boolean;
  /** Whether submission is in progress */
  isSubmitting?: boolean;
}

/**
 * Step Indicator Props
 * Props for the step progress indicator component
 */
export interface StepIndicatorProps {
  /** Array of step configurations */
  steps: FormStepConfig[];
  /** Current active step */
  currentStep: number;
  /** Step status array */
  stepStatuses: FormStepStatus[];
  /** Callback when a step is clicked */
  onStepClick?: (step: number) => void;
}
